/**
 * BestOddsBookmakerFilter - Multi-select by bestBookmaker for Market Anomalies.
 *
 * Independent from arbitrage FilterContext. Options are the full licensed
 * bookmaker catalog (BOOKMAKERS), not derived from the current anomalies list.
 * Semantics:
 * - selectedBookmakers === null → all (no bookmaker filter)
 * - string[] → only those bestBookmaker values (empty => no matches)
 */

import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useBestOdds } from '../context/BestOddsContext';
import { useTranslation } from '../../i18n/hooks';
import { BOOKMAKERS } from '../../surebets/constants/surebetTypes';
import './BestOddsBookmakerFilter.css';

const ALL_BOOKMAKERS = Object.keys(BOOKMAKERS)
  .map((id) => ({ id, name: BOOKMAKERS[id].name }))
  .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

const ALL_BOOKMAKER_IDS = ALL_BOOKMAKERS.map((item) => item.id);

const BestOddsBookmakerFilter = ({ className = '', showSectionLabel = true }) => {
  const { t } = useTranslation();
  const {
    filters,
    setSelectedBookmakers
  } = useBestOdds();

  const [localSearch, setLocalSearch] = useState('');

  const selectedBookmakers = filters?.selectedBookmakers;
  const isAllSelected = selectedBookmakers === null;

  const selectedCount = isAllSelected
    ? ALL_BOOKMAKER_IDS.length
    : (Array.isArray(selectedBookmakers) ? selectedBookmakers.length : 0);

  const isBookmakerSelected = useCallback((bookmakerId) => {
    if (selectedBookmakers === null) {
      return true;
    }
    if (!Array.isArray(selectedBookmakers)) {
      return true;
    }
    return selectedBookmakers.includes(bookmakerId);
  }, [selectedBookmakers]);

  const filteredBookmakers = useMemo(() => {
    const query = localSearch.trim().toLowerCase();
    if (!query) {
      return ALL_BOOKMAKERS;
    }
    return ALL_BOOKMAKERS.filter((bookmaker) =>
      bookmaker.name.toLowerCase().includes(query)
    );
  }, [localSearch]);

  const handleSelectAll = useCallback(() => {
    setSelectedBookmakers(null);
  }, [setSelectedBookmakers]);

  const handleDeselectAll = useCallback(() => {
    setSelectedBookmakers([]);
  }, [setSelectedBookmakers]);

  const handleToggle = useCallback((bookmakerId) => {
    if (selectedBookmakers === null || !Array.isArray(selectedBookmakers)) {
      setSelectedBookmakers(ALL_BOOKMAKER_IDS.filter((id) => id !== bookmakerId));
      return;
    }

    if (selectedBookmakers.includes(bookmakerId)) {
      setSelectedBookmakers(selectedBookmakers.filter((id) => id !== bookmakerId));
      return;
    }

    const next = [...selectedBookmakers, bookmakerId];
    const allSelected = ALL_BOOKMAKER_IDS.length > 0
      && ALL_BOOKMAKER_IDS.every((id) => next.includes(id));

    setSelectedBookmakers(allSelected ? null : next);
  }, [selectedBookmakers, setSelectedBookmakers]);

  return (
    <div
      className={`best-odds-bookmaker-filter ${className}`.trim()}
      role="group"
      aria-label={t('bestOdds.filters.bookmakersAriaLabel')}
    >
      <div className="best-odds-bookmaker-filter__header">
        {showSectionLabel ? (
          <span className="best-odds-bookmaker-filter__label">
            {t('bestOdds.filters.bookmakersLabel')}
            <span className="best-odds-bookmaker-filter__count">
              ({selectedCount}/{ALL_BOOKMAKER_IDS.length})
            </span>
          </span>
        ) : (
          <span className="best-odds-bookmaker-filter__label best-odds-bookmaker-filter__label--spacer" />
        )}
        <div className="best-odds-bookmaker-filter__actions">
          <button
            type="button"
            className="best-odds-bookmaker-filter__action-btn"
            onClick={handleSelectAll}
            disabled={isAllSelected}
          >
            {t('bestOdds.filters.bookmakersSelectAll')}
          </button>
          <button
            type="button"
            className="best-odds-bookmaker-filter__action-btn"
            onClick={handleDeselectAll}
            disabled={Array.isArray(selectedBookmakers) && selectedBookmakers.length === 0}
          >
            {t('bestOdds.filters.bookmakersDeselectAll')}
          </button>
        </div>
      </div>

      <div className="best-odds-bookmaker-filter__search">
        <input
          type="text"
          className="best-odds-bookmaker-filter__search-input"
          value={localSearch}
          onChange={(event) => setLocalSearch(event.target.value)}
          placeholder={t('bestOdds.filters.bookmakersSearchPlaceholder')}
          aria-label={t('bestOdds.filters.bookmakersSearchPlaceholder')}
          autoComplete="off"
        />
        {localSearch.trim() && (
          <button
            type="button"
            className="best-odds-bookmaker-filter__search-clear"
            onClick={() => setLocalSearch('')}
            aria-label={t('bestOdds.filters.searchClearAriaLabel')}
          >
            ×
          </button>
        )}
      </div>

      <div className="best-odds-bookmaker-filter__grid">
        {filteredBookmakers.map((bookmaker) => {
          const checked = isBookmakerSelected(bookmaker.id);
          return (
            <label
              key={bookmaker.id}
              className={`best-odds-bookmaker-filter__item${checked ? ' is-selected' : ''}`}
            >
              <input
                type="checkbox"
                className="best-odds-bookmaker-filter__checkbox"
                checked={checked}
                onChange={() => handleToggle(bookmaker.id)}
                value={bookmaker.id}
              />
              <span className="best-odds-bookmaker-filter__name">{bookmaker.name}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

BestOddsBookmakerFilter.propTypes = {
  className: PropTypes.string,
  showSectionLabel: PropTypes.bool
};

export default BestOddsBookmakerFilter;
